import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import './JaapaAnalyticsPage.css';

const JaapaAnalyticsPage = () => {
  const { t, language } = useLanguage();
  const [analytics, setAnalytics] = useState({ grandTotal: 0, grandTotalMalas: 0, breakdown: [] });
  const [period, setPeriod] = useState('today'); // 'today', 'week', 'month', 'all'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.getJaapaAnalytics(period);
        if (response.success) {
          setAnalytics(response.data);
        } else {
          throw new Error(response.message || t('jaapaAnalyticsPage.errors.fetchFailed'));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [period]); // Refetch data whenever the period changes

  const numberLocale = language === 'hi' ? 'hi-IN' : language === 'or' ? 'or-IN' : 'en-IN';

  const mantraLabelMap = {
    'Om Namaha Shivaya': 'omNamahaShivaya',
    'Om Namah Shivaya': 'omNamahShivaya',
    'Jai Shree Ram': 'jaiShreeRam',
    'Jai Shree Krishna': 'jaiShreeKrishna',
    'Shree Radha': 'shreeRadha',
    'Sita Ram': 'sitaRam',
    'Om Gan Ganapataye Namah': 'omGanGanapatayeNamah',
    'Om Rudraya Namah': 'omRudrayaNamah',
    'Radhe Krishna': 'radheKrishna',
    'Om Sai Ram': 'omSaiRam',
    'Om Kalabhiravay Namah': 'omKalabhiravayNamah',
    Shreem: 'shreem',
    Aim: 'aim',
    'Hare Krishna Hare Krishna\nKrishna Krishna Hare Hare\nHare Rama Hare Rama\nRama Rama Hare Hare': 'hareKrishnaMahaMantra',
    // Backward compatibility for older saved entries.
    'Hare Krishna': 'hareKrishna',
    'Shree Ram': 'shreeRam',
    'Shree Krishna': 'shreeKrishna',
    'Jai Maa Durga': 'jaiMaaDurga',
    'Maa Durga': 'maaDurga',
    'Jai Hanuman': 'jaiHanuman',
    Hanuman: 'hanuman',
  };

  const getTranslatedMantraName = (name) => {
    const key = mantraLabelMap[name];
    if (!key) return name;
    return t(`bhaktiJaapaPage.divineNames.${key}`, {}, name);
  };

  const filterOptions = [
    { id: 'today', label: t('jaapaAnalyticsPage.filters.today') },
    { id: 'week', label: t('jaapaAnalyticsPage.filters.thisWeek') },
    { id: 'month', label: t('jaapaAnalyticsPage.filters.thisMonth') },
    { id: 'all', label: t('jaapaAnalyticsPage.filters.allTime') },
  ];

  return (
    <div className="jaapa-analytics-container">
      <div className="analytics-header">
        <div className="analytics-title-group">
          <Link to="/bhakti-jaapa" className="back-to-jaapa-link">
            <i className="fas fa-arrow-left"></i> {t('jaapaAnalyticsPage.actions.backToBhaktiJaapa')}
          </Link>
          <h2>{t('jaapaAnalyticsPage.title')}</h2>
        </div>
        <div className="filter-buttons">
          {filterOptions.map(option => (
            <button
              key={option.id}
              className={`filter-btn ${period === option.id ? 'active' : ''}`}
              onClick={() => setPeriod(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="loading-spinner">{t('jaapaAnalyticsPage.loadingAnalytics')}</div>
      ) : error ? (
        <div className="error-message">{t('jaapaAnalyticsPage.errorPrefix')}: {error}</div>
      ) : (
        <>
          <div className="analytics-summary-card">
            <h3>{t('jaapaAnalyticsPage.cards.totalJaapas')}</h3>
            <p>{analytics.grandTotal.toLocaleString(numberLocale)}</p>
          </div>

          <div className="analytics-summary-card">
            <h3>{t('jaapaAnalyticsPage.cards.malasCompleted')}</h3>
            <p>{(analytics.grandTotalMalas || 0).toLocaleString(numberLocale)}</p>
          </div>

          <div className="analytics-breakdown">
            <h3>{t('jaapaAnalyticsPage.breakdown.title')}</h3>
            {analytics.breakdown.length > 0 ? (
              <div className="breakdown-list">
                {analytics.breakdown.map((item) => (
                  <div key={item.name} className="breakdown-item">
                    <span className="mantra-name">{getTranslatedMantraName(item.name)}</span>
                    <span className="mantra-count">
                      {item.count.toLocaleString(numberLocale)} {t('jaapaAnalyticsPage.breakdown.chants')}
                      {item.malas > 0 ? ` · ${item.malas.toLocaleString(numberLocale)} ${item.malas > 1 ? t('jaapaAnalyticsPage.breakdown.malasPlural') : t('jaapaAnalyticsPage.breakdown.malaSingular')}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                <p>{t('jaapaAnalyticsPage.breakdown.noData')}</p>
                <Link to="/bhakti-jaapa" className="btn btn-primary">{t('jaapaAnalyticsPage.breakdown.startChanting')}</Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default JaapaAnalyticsPage;
